import { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import { CenterpieceGreenLogo } from '../logos/CenterpieceGreenLogo';
import { CenterpieceGreenFlowerLogo } from '../logos/CenterpieceGreenFlowerLogo';
import {
  Button,
  Typography,
  Breadcrumbs,
  Link,
  Tooltip,
  useMediaQuery,
  useTheme,
  Badge,
  Box,
  ClickAwayListener,
  TextField,
} from '@mui/material';
import { ArrowBack, FolderOpen } from '@mui/icons-material';
import { HeaderMoreIcon, HeaderMoreIconProps } from './HeaderMoreIcon';
import { ChipButtonMenu, ChipButtonMenuProps } from '../drawers';
import { useBreakpointQuery } from '../../utils/media.util';
import { AvatarInputProps, AvatarsAutocompleteInput } from '../table';

export interface HeaderProjectProps extends HeaderMoreIconProps {
  title: string;
  status?: ChipButtonMenuProps;
  caption?: string;
  caption_details?: string;
  onLogoClick?: Function;
  onDashboardClick?: Function;
  onFolderClick?: Function;
  breadcrumbs?: { label: string; onClick?: Function }[];
  filter?: {
    options: string[];
    onChange: (event: React.SyntheticEvent, newValue: string | null) => void;
  };
  assignees?: AvatarInputProps;
  disabled?: boolean;
  menuBadge?: number;
  append?: any;
  save?: Function;
}

export const HeaderProject = ({
  title: orgTitle,
  status,
  caption,
  caption_details,
  onDashboardClick,
  onFolderClick,
  onLogoClick,
  tooltip,
  menuItems,
  breadcrumbs,
  assignees,
  disabled = false,
  filter,
  menuBadge,
  append,
  save,
}: HeaderProjectProps) => {
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down('sm'));
  const [hover, setHover] = useState<boolean>(false);
  const [isEditing, setEditing] = useState<boolean>(false);
  const [title, setTitle] = useState<string>(orgTitle);
  useEffect(() => {
    setTitle(orgTitle);
  }, [orgTitle]);
  return (
    <AppBar position="relative" sx={{ mb: '1%', backgroundColor: 'white' }}>
      <Toolbar
        disableGutters
        sx={{ minHeight: { sm: 100 } }}
        onMouseEnter={() => setHover(true && !disabled)}
        onMouseLeave={() => setHover(false)}
      >
        <Container
          maxWidth="xl"
          sx={{ backgroundColor: 'white', pt: 2, pb: 0 }}
        >
          <Grid container>
            <Grid item xs={12}>
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="start"
              >
                <Grid item sm={6}>
                  {breadcrumbs && breadcrumbs.length > 0 && (
                    <Breadcrumbs>
                      {breadcrumbs.map((breadcrumb, index) => {
                        return (
                          <Link
                            key={index}
                            underline="hover"
                            color="inherit"
                            onClick={() =>
                              breadcrumb.onClick && breadcrumb.onClick()
                            }
                          >
                            {breadcrumb.label}
                          </Link>
                        );
                      })}
                    </Breadcrumbs>
                  )}
                  <Box display={'flex'} alignItems={'start'}>
                    <Tooltip title={'Back to project index'}>
                      <Button
                        color="secondary"
                        startIcon={<ArrowBack />}
                        onClick={() => onLogoClick && onLogoClick()}
                        sx={{ pl: 0 }}
                      >
                        <CenterpieceGreenFlowerLogo height="44" width="44" />
                      </Button>
                    </Tooltip>
                    {/* <Typography
                      variant="h6"
                      sx={{
                        fontSize: { sm: 30, md: 40 },
                        fontWeight: 700,
                      }}
                      color={'primary'}
                    >
                      {title || ''}
                    </Typography> */}
                    {isEditing ? (
                      <ClickAwayListener
                        onClickAway={async () => {
                          if (save && isEditing) {
                            await save(title);
                          }
                          setEditing(false);
                        }}
                      >
                        <TextField
                          fullWidth={true}
                          InputProps={{
                            sx: {
                              fontSize: downSm ? 28 : 34,
                              fontWeight: 700,
                            },
                          }}
                          value={title}
                          onChange={event => setTitle(event.target.value)}
                        />
                      </ClickAwayListener>
                    ) : (
                      <Box
                        sx={{ clear: 'left' }}
                        onClick={() => {
                          if (save) {
                            setEditing(true);
                          }
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontSize: { sm: 30, md: 40 },
                            fontWeight: 700,
                          }}
                          color={'primary'}
                        >
                          {title || ''}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid item sm={4}>
                  <Grid
                    container
                    direction="row"
                    justifyContent="right"
                    alignItems="center"
                    wrap="nowrap"
                  >
                    <Typography
                      variant="h5"
                      sx={{ fontSize: { xs: 12, sm: 16 }, pr: 1 }}
                      color={'primary'}
                    >
                      <u>{caption}</u>
                      {caption && ':'}{' '}
                      <small>
                        <i>{caption_details}</i>
                      </small>
                    </Typography>
                    <Grid item sx={{ mr: 1 }}>
                      <AvatarsAutocompleteInput
                        {...assignees}
                        disabled={disabled || assignees?.disabled}
                        hover={hover}
                      />
                    </Grid>
                    {status && (
                      <Grid item>
                        <ChipButtonMenu {...status} />
                      </Grid>
                    )}
                    <div id={'header-project-menu'}>
                      <Badge badgeContent={menuBadge} color="secondary">
                        <HeaderMoreIcon
                          tooltip={tooltip}
                          menuItems={menuItems}
                          color={'primary'}
                        />
                      </Badge>
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {append && (
              <Grid item sx={{ pt: 3 }}>
                {append}
              </Grid>
            )}
          </Grid>
        </Container>
      </Toolbar>
    </AppBar>
  );
};

export interface HeaderProjectPreviewProps {
  title: string;
  onBack?: Function;
}

export const HeaderProjectPreview = ({
  title,
  onBack,
}: HeaderProjectPreviewProps) => {
  const smDown = useBreakpointQuery('sm-down');
  return (
    <AppBar position="relative" sx={{ mb: '1%', backgroundColor: 'white' }}>
      <Toolbar disableGutters>
        <Container maxWidth="xl" sx={{ backgroundColor: 'white', py: 2 }}>
          <Tooltip title={'Back to previous'}>
            <Button
              color="secondary"
              startIcon={
                <div style={{ marginTop: '4px' }}>
                  <ArrowBack />
                </div>
              }
              onClick={() => onBack && onBack()}
              sx={{ pl: 0 }}
            >
              <Typography variant="h5" color={'primary'}>
                {title || ''}
              </Typography>
            </Button>
          </Tooltip>
        </Container>
      </Toolbar>
    </AppBar>
  );
};
